/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import type { CustomTableLayout, StyleDictionary } from "pdfmake/interfaces";

/**
 * Page geometry and visual styling for the generated documentation PDF.
 *
 * The PDF is deliberately printed as black text on a white page rather than
 * following the active Dexter theme — a dark mode PDF wastes toner and reads
 * poorly on paper.
 */

/** US Letter dimensions in PDF points (72pt = 1 inch). */
export const PAGE_WIDTH = 612;
export const PAGE_HEIGHT = 792;
export const PAGE_MARGIN = 56;

/** Usable horizontal space between the left and right margins. */
export const CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN * 2;

/**
 * Upper bound on the rendered height of a single screenshot. Anything taller is
 * scaled down so a tall image fits on one page instead of being clipped. The
 * extra allowance leaves room for the footer.
 */
export const MAX_IMAGE_HEIGHT = PAGE_HEIGHT - PAGE_MARGIN * 2 - 24;

/** CSS pixels to PDF points. */
export const PX_TO_PT = 0.75;

export const COLORS = {
  text: "#000000",
  muted: "#444444",
  rule: "#BBBBBB",
  codeBackground: "#F2F2F2",
  tableHeaderBackground: "#EEEEEE",
  link: "#0B5FA5",
} as const;

/**
 * Font family name as registered in pdfmake's virtual file system. Roboto is
 * what pdfmake bundles by default.
 */
export const BODY_FONT = "Roboto";

/**
 * Face used for fenced code blocks and inline code. pdfmake's bundled virtual
 * file system contains no monospace font, so this currently falls back to the
 * body face. Registering a monospace TTF in the vfs and changing this constant
 * is the only edit required to switch.
 */
export const CODE_FONT = BODY_FONT;

export const pdfStyles: StyleDictionary = {
  // Cover page
  coverTitle: {
    fontSize: 40,
    bold: true,
    alignment: "center",
    margin: [0, 0, 0, 4],
  },
  coverSubtitle: {
    fontSize: 18,
    alignment: "center",
    color: COLORS.muted,
    margin: [0, 0, 0, 24],
  },
  coverDescription: {
    fontSize: 11,
    alignment: "center",
    lineHeight: 1.4,
    margin: [48, 0, 48, 28],
  },
  coverMeta: {
    fontSize: 10,
    alignment: "center",
    color: COLORS.muted,
    margin: [0, 0, 0, 2],
  },
  coverLegend: {
    fontSize: 8.5,
    alignment: "justify",
    color: COLORS.muted,
    lineHeight: 1.35,
    margin: [32, 36, 32, 0],
  },

  // Table of contents
  tocTitle: {
    fontSize: 22,
    bold: true,
    margin: [0, 0, 0, 16],
  },

  // Body headings. h1 is the section title supplied by DocSections; the
  // markdown content itself starts at h2.
  h1: {
    fontSize: 24,
    bold: true,
    margin: [0, 0, 0, 14],
  },
  h2: {
    fontSize: 17,
    bold: true,
    margin: [0, 18, 0, 8],
  },
  h3: {
    fontSize: 13.5,
    bold: true,
    margin: [0, 14, 0, 6],
  },
  h4: {
    fontSize: 11.5,
    bold: true,
    margin: [0, 10, 0, 4],
  },

  // Body content
  paragraph: {
    fontSize: 10.5,
    lineHeight: 1.35,
    margin: [0, 0, 0, 8],
  },
  list: {
    fontSize: 10.5,
    lineHeight: 1.35,
    margin: [0, 0, 0, 8],
  },
  code: {
    font: CODE_FONT,
    fontSize: 9,
    lineHeight: 1.25,
    preserveLeadingSpaces: true,
  },
  inlineCode: {
    font: CODE_FONT,
    fontSize: 9.5,
    background: COLORS.codeBackground,
  },
  link: {
    color: COLORS.link,
    decoration: "underline",
  },
  tableHeader: {
    fontSize: 10,
    bold: true,
    fillColor: COLORS.tableHeaderBackground,
  },
  tableCell: {
    fontSize: 10,
  },
  imageCaption: {
    fontSize: 9,
    italics: true,
    alignment: "center",
    color: COLORS.muted,
    margin: [0, 0, 0, 12],
  },
  footer: {
    fontSize: 8.5,
    color: COLORS.muted,
  },
};

/** Borderless, filled single-cell table used as the backdrop for code blocks. */
export const CODE_BLOCK_LAYOUT: CustomTableLayout = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  fillColor: () => COLORS.codeBackground,
  paddingLeft: () => 10,
  paddingRight: () => 10,
  paddingTop: () => 8,
  paddingBottom: () => 8,
};

/** Hairline grid used for markdown tables. */
export const TABLE_LAYOUT: CustomTableLayout = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => COLORS.rule,
  vLineColor: () => COLORS.rule,
  paddingLeft: () => 6,
  paddingRight: () => 6,
  paddingTop: () => 5,
  paddingBottom: () => 5,
};
